import type { FirmClient } from '../../../generated/graphql';
import {
  EstateStatusReadOnlySummary,
  useEstateStatusReadOnlyHouseholdSettings,
} from '../../../EstateStatus';
import { useFeatureFlagsContext } from '../../../FeatureFlags';
import { elementsColors } from '../../../util';
import { ElementDetailViewHeader, ElementDetailViewIndexPageContainer, SideOver } from '../../components';
import { LocalThemeContextProvider } from '../../context';
import { Outlet } from '@remix-run/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface EstateStatusReadOnlyElementDetailViewLayoutPageProps {
  baseRoute: string;
  exitRoute?: string;
  children?: ReactNode;
}

export interface EstateStatusReadOnlyElementDetailViewIndexPageProps {
  firmID: string;
  householdID: string;
  firmClient?: FirmClient | null;
}

export function EstateStatusReadOnlyElementDetailViewLayoutPage({
  baseRoute,
  exitRoute,
  children,
}: EstateStatusReadOnlyElementDetailViewLayoutPageProps) {
  return (
    <SideOver baseRoute={baseRoute} exitRoute={exitRoute} className="!px-0">
      <LocalThemeContextProvider paletteSeedColor={elementsColors.Es}>
        {children ?? <Outlet />}
      </LocalThemeContextProvider>
    </SideOver>
  );
}

function EstateStatusReadOnlyMessage({ message }: { message: string }) {
  const { t } = useTranslation('estateStatus');

  return (
    <ElementDetailViewIndexPageContainer>
      <ElementDetailViewHeader title={t('title')} tooltipDescription={t('header-tooltip-description')} />
      <p className="pb-10 text-sm text-darkMode-gray-light/80">{message}</p>
    </ElementDetailViewIndexPageContainer>
  );
}

export function EstateStatusReadOnlyElementDetailViewIndexPage({
  firmID,
  householdID,
  firmClient,
}: EstateStatusReadOnlyElementDetailViewIndexPageProps) {
  const { t } = useTranslation('estateStatus');
  const { allowEstateStatus, allowTargetScoreRanges } = useFeatureFlagsContext();
  const estateStatusHouseholdID = allowEstateStatus === true ? householdID : undefined;
  const { estateStatusHouseholdSettings, estateStatusHouseholdSettingsQuery } =
    useEstateStatusReadOnlyHouseholdSettings(estateStatusHouseholdID);

  if (allowEstateStatus !== true) {
    return <EstateStatusReadOnlyMessage message={t('disabled-message')} />;
  }

  if (estateStatusHouseholdSettingsQuery.isLoading) {
    return <EstateStatusReadOnlyMessage message={t('loading-message')} />;
  }

  if (estateStatusHouseholdSettingsQuery.isError) {
    return <EstateStatusReadOnlyMessage message={t('load-error-message')} />;
  }

  if (!estateStatusHouseholdSettings?.checklist?.length) {
    return <EstateStatusReadOnlyMessage message={t('client-empty-message')} />;
  }

  return (
    <ElementDetailViewIndexPageContainer>
      <ElementDetailViewHeader title={t('title')} tooltipDescription={t('header-tooltip-description')} />
      <EstateStatusReadOnlySummary
        checklist={estateStatusHouseholdSettings.checklist}
        firmClientID={firmClient?.id}
        firmID={firmClient?.firmID ?? firmID}
        showScoreHealthRange={allowTargetScoreRanges}
      />
    </ElementDetailViewIndexPageContainer>
  );
}
